/**
 * Cliente HTTP do Lumma Store.
 *
 * Um só ponto de saída para a API. Todo `fetch` do sistema passa por aqui, e
 * por isso credencial, erro e id de correlação são tratados em um lugar só —
 * em vez de vinte tratamentos ligeiramente diferentes espalhados nas telas.
 */

/** Código de erro estável devolvido pela API. */
export type CodigoErro =
  | 'requisicao_invalida'
  | 'validacao'
  | 'nao_autenticado'
  | 'sessao_invalida'
  | 'sem_permissao'
  | 'nao_encontrado'
  | 'conflito'
  /**
   * 409 da spec 04: a ação é permitida para este papel, mas exige a
   * autorização de um terceiro por PIN. `campos.requires_pin` traz a CHAVE da
   * permissão — não só "faltou algo" —, e é isso que permite à tela dizer o
   * que está sendo autorizado.
   */
  | 'requires_pin'
  | 'pin_invalido'
  | 'muitas_tentativas'
  | 'erro_interno'
  | 'indisponivel'

/** Erro da API já traduzido para o front. */
export class ErroApi extends Error {
  readonly codigo: CodigoErro
  readonly status: number
  readonly campos: Record<string, string>
  readonly idCorrelacao: string

  constructor(params: {
    codigo: CodigoErro
    status: number
    mensagem: string
    campos?: Record<string, string>
    idCorrelacao?: string
  }) {
    super(params.mensagem)
    this.name = 'ErroApi'
    this.codigo = params.codigo
    this.status = params.status
    this.campos = params.campos ?? {}
    this.idCorrelacao = params.idCorrelacao ?? ''
  }

  /**
   * Diz se o erro significa "a sessão morreu, vá para o login".
   *
   * É diferente de `nao_autenticado`, que é "a senha está errada, fique na
   * tela de login". Tratar os dois igual faria a tela de login expulsar o
   * usuário para ela mesma a cada senha errada.
   */
  get sessaoMorreu(): boolean {
    return this.codigo === 'sessao_invalida'
  }

  /**
   * Diz se falta a autorização de um terceiro, e devolve a chave da permissão.
   *
   * Vazio quando não é esse caso. A chave importa: a tela mostra "autorizar
   * ajuste de estoque" em vez de "digite um PIN", e a pessoa que vai digitar
   * sabe o que está liberando.
   */
  get pinNecessario(): string {
    if (this.codigo !== 'requires_pin') return ''
    return this.campos.requires_pin ?? ''
  }
}

type CorpoErro = {
  erro?: {
    codigo?: CodigoErro
    mensagem?: string
    campos?: Record<string, string>
    id_correlacao?: string
  }
}

const BASE = '/api/v1'

/** Chamado quando a sessão morre em qualquer requisição. */
let aoPerderSessao: (() => void) | null = null

/** Registra o que fazer quando a API disser que a sessão morreu. */
export function registrarPerdaDeSessao(acao: () => void): void {
  aoPerderSessao = acao
}

/** Cabeçalho da autorização por PIN — espelha porteiro.CabecalhoPIN. */
const CABECALHO_PIN = 'X-Autorizacao-PIN'

type Opcoes = {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  corpo?: unknown
  /**
   * Corpo BRUTO, enviado como está — sem `JSON.stringify` — com o
   * `Content-Type` de `tipoConteudo`. Existe para upload de arquivo, hoje só
   * o XML da NF-e (`notasfiscais.ts`): o corpo ali já É o texto a enviar, e
   * envolvê-lo em JSON mudaria o que a API lê.
   *
   * Mutuamente exclusivo com `corpo` — quem manda `corpoBruto` não manda
   * `corpo`.
   */
  corpoBruto?: BodyInit
  tipoConteudo?: string
  /** Sinal para cancelar a requisição quando a tela é desmontada. */
  sinal?: AbortSignal
  /**
   * PIN de 4 dígitos do autorizador, quando a ação exigiu 409 requires_pin.
   *
   * Vai em CABEÇALHO e não no corpo, de propósito: o corpo do pedido é a
   * intenção do usuário e vai para o log de auditoria; o PIN é credencial e
   * não pode acabar registrado junto do que foi lançado.
   */
  pin?: string
}

/**
 * Chama a API e devolve o corpo já decodificado.
 *
 * `credentials: 'same-origin'` é o que faz o cookie de sessão viajar. Em dev,
 * o Vite encaminha /api para a porta 8080, então a origem é a mesma que em
 * produção — o cookie se comporta igual nos dois ambientes.
 */
export async function chamar<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const { metodo = 'GET', corpo, corpoBruto, tipoConteudo, sinal, pin } = opcoes

  const cabecalhos: Record<string, string> = {}
  if (corpoBruto !== undefined) {
    if (tipoConteudo) cabecalhos['Content-Type'] = tipoConteudo
  } else if (corpo !== undefined) {
    cabecalhos['Content-Type'] = 'application/json'
  }
  if (pin) cabecalhos[CABECALHO_PIN] = pin

  let resposta: Response
  try {
    resposta = await fetch(BASE + caminho, {
      method: metodo,
      credentials: 'same-origin',
      headers: cabecalhos,
      body: corpoBruto !== undefined ? corpoBruto : corpo === undefined ? undefined : JSON.stringify(corpo),
      signal: sinal ?? null,
    })
  } catch (causa) {
    if (causa instanceof DOMException && causa.name === 'AbortError') throw causa
    throw new ErroApi({
      codigo: 'indisponivel',
      status: 0,
      mensagem: 'Não foi possível falar com o servidor. Verifique sua conexão.',
    })
  }

  const idCorrelacao = resposta.headers.get('X-Id-Correlacao') ?? ''

  if (resposta.status === 204) {
    return undefined as T
  }

  const texto = await resposta.text()
  let dados: unknown = null
  if (texto.length > 0) {
    try {
      dados = JSON.parse(texto)
    } catch {
      dados = null
    }
  }

  if (!resposta.ok) {
    const corpoErro = (dados ?? {}) as CorpoErro
    const erro = new ErroApi({
      codigo: corpoErro.erro?.codigo ?? 'erro_interno',
      status: resposta.status,
      mensagem: corpoErro.erro?.mensagem ?? 'Não foi possível concluir a operação.',
      campos: corpoErro.erro?.campos ?? {},
      idCorrelacao: corpoErro.erro?.id_correlacao ?? idCorrelacao,
    })

    if (erro.sessaoMorreu) aoPerderSessao?.()
    throw erro
  }

  return dados as T
}

export const api = {
  get: <T>(caminho: string, sinal?: AbortSignal) => chamar<T>(caminho, { metodo: 'GET', sinal }),
  post: <T>(caminho: string, corpo?: unknown, pin?: string) =>
    chamar<T>(caminho, { metodo: 'POST', corpo, pin }),
  put: <T>(caminho: string, corpo?: unknown, pin?: string) =>
    chamar<T>(caminho, { metodo: 'PUT', corpo, pin }),
  delete: <T>(caminho: string) => chamar<T>(caminho, { metodo: 'DELETE' }),
  /** POST de corpo bruto (upload de arquivo) — ver `Opcoes.corpoBruto`. */
  postArquivo: <T>(caminho: string, corpoBruto: BodyInit, tipoConteudo: string, sinal?: AbortSignal) =>
    chamar<T>(caminho, { metodo: 'POST', corpoBruto, tipoConteudo, sinal }),
}
